import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/shared/services/queryClient";
import {
  reserveReleaseApi,
  type CreateReserveReleaseRequest,
} from "../services/reserveReleaseApi";

const PENDING_QUERY_KEY = ["reserve-releases", "pending"] as const;

const invalidateStockAndPending = () => {
  queryClient.invalidateQueries({ queryKey: PENDING_QUERY_KEY });
  queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-entries"] });
  queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-available"] });
  queryClient.invalidateQueries({ queryKey: ["greenhouse-stock-by-variety"] });
  queryClient.invalidateQueries({ queryKey: ["reserve-releases", "history"] });
};

export const usePendingReserveReleases = () =>
  useQuery({
    queryKey: PENDING_QUERY_KEY,
    queryFn: () => reserveReleaseApi.pending(),
  });

export const useReserveReleaseHistory = (entryId: string | undefined) =>
  useQuery({
    queryKey: ["reserve-releases", "history", entryId],
    queryFn: () => reserveReleaseApi.history(entryId!),
    enabled: !!entryId,
  });

export const useRequestReserveRelease = () =>
  useMutation({
    mutationFn: ({
      entryId,
      body,
    }: {
      entryId: string;
      body: CreateReserveReleaseRequest;
    }) => reserveReleaseApi.request(entryId, body),
    onSuccess: invalidateStockAndPending,
  });

export const useApproveReserveRelease = () =>
  useMutation({
    mutationFn: ({ id, decisionComment }: { id: string; decisionComment?: string }) =>
      reserveReleaseApi.approve(id, { decisionComment }),
    onSuccess: invalidateStockAndPending,
  });

export const useRejectReserveRelease = () =>
  useMutation({
    mutationFn: ({ id, decisionComment }: { id: string; decisionComment: string }) =>
      reserveReleaseApi.reject(id, { decisionComment }),
    onSuccess: invalidateStockAndPending,
  });
